import React, { useState, useEffect } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { useNotificationActions } from '@/contexts/NotificationContext'
import { authService } from '@/services/authService'

interface ProfileData {
  name: string
  email: string
  phone: string
  role: string
  avatar?: string
  bio: string
  preferences: {
    theme: 'light' | 'dark' | 'auto'
    language: string
    timezone: string
    notifications: {
      email: boolean
      sms: boolean
      push: boolean
    }
  }
}

export const ProfilePage: React.FC = () => {
  const { user } = useAuth()
  const { showSuccess, showError } = useNotificationActions()
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [activeTab, setActiveTab] = useState('personal')
  const [changePasswordData, setChangePasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  })
  const [changingPassword, setChangingPassword] = useState(false)

  const getUserName = () => {
    // AuthContext User has 'name' property, authStore User has firstName/lastName
    if ((user as any)?.name) {
      return (user as any).name
    }
    if ((user as any)?.firstName && (user as any)?.lastName) {
      return `${(user as any).firstName} ${(user as any).lastName}`
    }
    return user?.email || ''
  }

  const [profileData, setProfileData] = useState<ProfileData>({
    name: getUserName(),
    email: user?.email || '',
    phone: '',
    role: user?.role || 'cashier',
    avatar: '',
    bio: '',
    preferences: {
      theme: 'dark',
      language: 'en',
      timezone: 'America/New_York',
      notifications: {
        email: true,
        sms: false,
        push: true
      }
    }
  })

  const tabs = [
    { id: 'personal', name: 'Personal Info', icon: '👤' },
    { id: 'preferences', name: 'Preferences', icon: '⚙️' },
    { id: 'security', name: 'Security', icon: '🔒' },
    { id: 'notifications', name: 'Notifications', icon: '🔔' }
  ]

  const languages = [
    { code: 'en', name: 'English' },
    { code: 'es', name: 'Spanish' },
    { code: 'fr', name: 'French' },
    { code: 'de', name: 'German' },
    { code: 'zh', name: 'Chinese' }
  ]

  const timezones = [
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Los_Angeles',
    'Europe/London',
    'Europe/Paris',
    'Asia/Tokyo'
  ]

  useEffect(() => {
    fetchProfileData()
  }, [])

  const fetchProfileData = async () => {
    setLoading(true)
    try {
      const response = await authService.getProfile()
      if (response.success && response.data) {
        const userData = response.data
        const userName = (userData as any).name ||
          (userData.firstName && userData.lastName
            ? `${userData.firstName} ${userData.lastName}`
            : user?.email || '')

        setProfileData({
          name: userName,
          email: userData.email || user?.email || '',
          phone: (userData as any).phone || '',
          role: userData.role || user?.role || 'cashier',
          avatar: userData.avatar || '',
          bio: (userData as any).bio || '',
          preferences: (userData as any).preferences || {
            theme: 'dark',
            language: 'en',
            timezone: 'America/New_York',
            notifications: {
              email: true,
              sms: false,
              push: true
            }
          }
        })
      }
    } catch (error) {
      showError('Failed to load profile', 'Please try again later.')
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      // Convert profileData to User format
      const [firstName, ...lastNameParts] = profileData.name.split(' ')
      const lastName = lastNameParts.join(' ') || ''

      const updateData = {
        firstName: firstName || '',
        lastName: lastName,
        email: profileData.email,
        role: profileData.role as 'admin' | 'manager' | 'cashier',
        phone: profileData.phone,
        bio: profileData.bio,
        preferences: profileData.preferences,
      }

      const response = await authService.updateProfile(updateData)
      if (response.success) {
        showSuccess('Profile updated successfully', 'Your changes have been saved.')
      } else {
        showError('Failed to update profile', 'Please try again later.')
      }
    } catch (error) {
      showError('Failed to update profile', 'Please try again later.')
    } finally {
      setSaving(false)
    }
  }

  const handleNotificationToggle = async (notificationType: 'email' | 'sms' | 'push', value: boolean) => {
    // Update local state immediately for responsive UI
    setProfileData(prev => ({
      ...prev,
      preferences: {
        ...prev.preferences,
        notifications: {
          ...prev.preferences.notifications,
          [notificationType]: value
        }
      }
    }))

    // Save to backend immediately
    try {
      const [firstName, ...lastNameParts] = profileData.name.split(' ')
      const lastName = lastNameParts.join(' ') || ''

      const updatedData = {
        firstName: firstName || '',
        lastName: lastName,
        email: profileData.email,
        role: profileData.role as 'admin' | 'manager' | 'cashier',
        phone: profileData.phone,
        bio: profileData.bio,
        preferences: {
          ...profileData.preferences,
          notifications: {
            ...profileData.preferences.notifications,
            [notificationType]: value
          }
        }
      }

      const response = await authService.updateProfile(updatedData)
      if (response.success) {
        showSuccess('Notification preference saved', `${notificationType} notifications ${value ? 'enabled' : 'disabled'}.`)
      } else {
        // Revert on failure
        setProfileData(prev => ({
          ...prev,
          preferences: {
            ...prev.preferences,
            notifications: {
              ...prev.preferences.notifications,
              [notificationType]: !value
            }
          }
        }))
        showError('Failed to save notification preference', 'Please try again.')
      }
    } catch (error) {
      // Revert on error
      setProfileData(prev => ({
        ...prev,
        preferences: {
          ...prev.preferences,
          notifications: {
            ...prev.preferences.notifications,
            [notificationType]: !value
          }
        }
      }))
      showError('Failed to save notification preference', 'Please try again.')
    }
  }

  const handleAvatarUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      try {
        const response = await authService.uploadAvatar(file)
        if (response.success && response.data) {
          setProfileData(prev => ({
            ...prev,
            avatar: response.data.avatar
          }))
          showSuccess('Avatar updated', 'Your profile picture has been changed.')
        } else {
          showError('Failed to upload avatar', 'Please try again.')
        }
      } catch (error) {
        showError('Failed to upload avatar', 'Please try again.')
      }
    }
  }

  const renderPersonalInfo = () => (
    <div className="space-y-6">
      {/* Avatar Section */}
      <div className="flex items-center space-x-6">
        <div className="relative">
          <div className="w-24 h-24 rounded-full overflow-hidden border-4 border-border">
            {profileData.avatar ? (
              <img
                src={profileData.avatar}
                alt="Profile"
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                <span className="text-white text-3xl font-bold">{profileData.name?.charAt(0) || 'U'}</span>
              </div>
            )}
          </div>
          <label className="absolute bottom-0 right-0 w-8 h-8 bg-primary rounded-full flex items-center justify-center cursor-pointer hover:bg-primary/90 transition-colors">
            <span className="text-white text-sm">📷</span>
            <input
              type="file"
              accept="image/*"
              onChange={handleAvatarUpload}
              className="hidden"
            />
          </label>
        </div>
        <div>
          <h3 className="text-xl font-bold text-gradient-esales">{profileData.name}</h3>
          <p className="text-muted-foreground">{profileData.email}</p>
          <span className="inline-block px-3 py-1 bg-primary/20 text-primary text-sm rounded-full mt-2">
            {profileData.role}
          </span>
        </div>
      </div>

      {/* Personal Information Form */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-muted-foreground text-sm font-medium mb-2">Full Name</label>
          <input
            type="text"
            value={profileData.name}
            onChange={(e) => setProfileData(prev => ({ ...prev, name: e.target.value }))}
            className="w-full px-4 py-3 bg-background/50 border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
        <div>
          <label className="block text-muted-foreground text-sm font-medium mb-2">Email Address</label>
          <input
            type="email"
            value={profileData.email}
            onChange={(e) => setProfileData(prev => ({ ...prev, email: e.target.value }))}
            className="w-full px-4 py-3 bg-background/50 border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
        <div>
          <label className="block text-muted-foreground text-sm font-medium mb-2">Phone Number</label>
          <input
            type="tel"
            value={profileData.phone}
            onChange={(e) => setProfileData(prev => ({ ...prev, phone: e.target.value }))}
            className="w-full px-4 py-3 bg-background/50 border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
        <div>
          <label className="block text-muted-foreground text-sm font-medium mb-2">Role</label>
          <select
            value={profileData.role}
            onChange={(e) => setProfileData(prev => ({ ...prev, role: e.target.value }))}
            className="w-full px-4 py-3 bg-background/50 border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="cashier">Cashier</option>
            <option value="manager">Manager</option>
            <option value="admin">Admin</option>
          </select>
        </div>
      </div>

      <div>
        <label className="block text-muted-foreground text-sm font-medium mb-2">Bio</label>
        <textarea
          value={profileData.bio}
          onChange={(e) => setProfileData(prev => ({ ...prev, bio: e.target.value }))}
          rows={4}
          className="w-full px-4 py-3 bg-background/50 border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          placeholder="Tell us about yourself..."
        />
      </div>
    </div>
  )

  const renderPreferences = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-muted-foreground text-sm font-medium mb-2">Theme</label>
        <div className="grid grid-cols-3 gap-4">
          {[
            { value: 'light', label: 'Light', icon: '☀️' },
            { value: 'dark', label: 'Dark', icon: '🌙' },
            { value: 'auto', label: 'Auto', icon: '🔄' }
          ].map((theme) => (
            <button
              key={theme.value}
              onClick={() => setProfileData(prev => ({
                ...prev,
                preferences: { ...prev.preferences, theme: theme.value as any }
              }))}
              className={`p-4 rounded-lg border transition-all ${profileData.preferences.theme === theme.value
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-border bg-background/50 text-foreground hover:bg-background/80'
                }`}
            >
              <div className="text-2xl mb-2">{theme.icon}</div>
              <div className="text-sm font-medium">{theme.label}</div>
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-muted-foreground text-sm font-medium mb-2">Language</label>
          <select
            value={profileData.preferences.language}
            onChange={(e) => setProfileData(prev => ({
              ...prev,
              preferences: { ...prev.preferences, language: e.target.value }
            }))}
            className="w-full px-4 py-3 bg-background/50 border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          >
            {languages.map(lang => (
              <option key={lang.code} value={lang.code}>{lang.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-muted-foreground text-sm font-medium mb-2">Timezone</label>
          <select
            value={profileData.preferences.timezone}
            onChange={(e) => setProfileData(prev => ({
              ...prev,
              preferences: { ...prev.preferences, timezone: e.target.value }
            }))}
            className="w-full px-4 py-3 bg-background/50 border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          >
            {timezones.map(tz => (
              <option key={tz} value={tz}>{tz}</option>
            ))}
          </select>
        </div>
      </div>
    </div>
  )

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault()
    const { currentPassword, newPassword, confirmPassword } = changePasswordData
    if (!currentPassword || !newPassword || !confirmPassword) {
      showError('Missing fields', 'Please fill in all password fields.')
      return
    }
    if (newPassword !== confirmPassword) {
      showError('Mismatch', 'New password and confirmation do not match.')
      return
    }
    if (newPassword.length < 6) {
      showError('Weak password', 'New password must be at least 6 characters.')
      return
    }
    setChangingPassword(true)
    try {
      await authService.changePassword({ currentPassword, newPassword, confirmPassword })
      setChangePasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' })
      showSuccess('Password changed', 'Your password has been updated successfully.')
    } catch (err: any) {
      showError('Change failed', err?.message || 'Could not change password.')
    } finally {
      setChangingPassword(false)
    }
  }

  const renderSecurity = () => (
    <div className="space-y-6">
      <form onSubmit={handleChangePassword} className="space-y-6">
        <div>
          <label className="block text-muted-foreground text-sm font-medium mb-2">Current Password</label>
          <input
            type="password"
            value={changePasswordData.currentPassword}
            onChange={(e) => setChangePasswordData((p) => ({ ...p, currentPassword: e.target.value }))}
            className="w-full px-4 py-3 bg-background/50 border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="Enter current password"
          />
        </div>
        <div>
          <label className="block text-muted-foreground text-sm font-medium mb-2">New Password</label>
          <input
            type="password"
            value={changePasswordData.newPassword}
            onChange={(e) => setChangePasswordData((p) => ({ ...p, newPassword: e.target.value }))}
            className="w-full px-4 py-3 bg-background/50 border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="At least 6 characters"
          />
        </div>
        <div>
          <label className="block text-muted-foreground text-sm font-medium mb-2">Confirm New Password</label>
          <input
            type="password"
            value={changePasswordData.confirmPassword}
            onChange={(e) => setChangePasswordData((p) => ({ ...p, confirmPassword: e.target.value }))}
            className="w-full px-4 py-3 bg-background/50 border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="Confirm new password"
          />
        </div>
        <button
          type="submit"
          disabled={changingPassword}
          className="bg-gradient-to-r from-primary to-secondary text-white font-semibold py-2 px-6 rounded-lg hover:bg-primary/90 hover:to-secondary/90 transition-all duration-300 disabled:opacity-50"
        >
          {changingPassword ? 'Updating...' : 'Change Password'}
        </button>
      </form>

      <div className="bg-background/50 rounded-lg p-4">
        <h4 className="text-foreground font-medium mb-2">Two-Factor Authentication</h4>
        <p className="text-muted-foreground text-sm mb-3">Add an extra layer of security to your account</p>
        <button type="button" className="bg-muted text-muted-foreground font-semibold py-2 px-4 rounded-lg cursor-not-allowed" disabled title="Coming soon">
          Enable 2FA (coming soon)
        </button>
      </div>
    </div>
  )

  const renderNotifications = () => (
    <div className="space-y-6">
      <div className="bg-background/30 rounded-lg p-4 mb-6">
        <h4 className="text-foreground font-medium mb-2">📱 Notification Preferences</h4>
        <p className="text-muted-foreground text-sm">Your notification preferences are saved automatically when you toggle them.</p>
      </div>

      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-foreground font-medium">Email Notifications</h3>
          <p className="text-muted-foreground text-sm">Receive notifications via email</p>
        </div>
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={profileData.preferences.notifications.email}
            onChange={(e) => handleNotificationToggle('email', e.target.checked)}
            className="sr-only peer"
          />
          <div className="w-11 h-6 bg-background/50 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-border after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
        </label>
      </div>

      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-foreground font-medium">SMS Notifications</h3>
          <p className="text-muted-foreground text-sm">Receive notifications via SMS</p>
        </div>
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={profileData.preferences.notifications.sms}
            onChange={(e) => handleNotificationToggle('sms', e.target.checked)}
            className="sr-only peer"
          />
          <div className="w-11 h-6 bg-background/50 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-border after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
        </label>
      </div>

      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-foreground font-medium">Push Notifications</h3>
          <p className="text-muted-foreground text-sm">Receive push notifications in browser</p>
        </div>
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={profileData.preferences.notifications.push}
            onChange={(e) => handleNotificationToggle('push', e.target.checked)}
            className="sr-only peer"
          />
          <div className="w-11 h-6 bg-background/50 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-border after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
        </label>
      </div>

      {/* Additional notification settings */}
      <div className="border-t border-border pt-6 mt-6">
        <h4 className="text-foreground font-medium mb-4">🔔 Notification Types</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-center justify-between p-3 bg-background/30 rounded-lg">
            <div>
              <p className="text-foreground text-sm font-medium">Order Updates</p>
              <p className="text-muted-foreground text-xs">New orders, cancellations</p>
            </div>
            <div className="w-8 h-4 bg-primary rounded-full flex items-center justify-end px-1">
              <div className="w-3 h-3 bg-white rounded-full"></div>
            </div>
          </div>
          <div className="flex items-center justify-between p-3 bg-background/30 rounded-lg">
            <div>
              <p className="text-foreground text-sm font-medium">Inventory Alerts</p>
              <p className="text-muted-foreground text-xs">Low stock warnings</p>
            </div>
            <div className="w-8 h-4 bg-primary rounded-full flex items-center justify-end px-1">
              <div className="w-3 h-3 bg-white rounded-full"></div>
            </div>
          </div>
          <div className="flex items-center justify-between p-3 bg-background/30 rounded-lg">
            <div>
              <p className="text-foreground text-sm font-medium">System Updates</p>
              <p className="text-muted-foreground text-xs">App updates, maintenance</p>
            </div>
            <div className="w-8 h-4 bg-background/50 rounded-full flex items-center justify-start px-1">
              <div className="w-3 h-3 bg-muted-foreground rounded-full"></div>
            </div>
          </div>
          <div className="flex items-center justify-between p-3 bg-background/30 rounded-lg">
            <div>
              <p className="text-foreground text-sm font-medium">Marketing</p>
              <p className="text-muted-foreground text-xs">Promotions, offers</p>
            </div>
            <div className="w-8 h-4 bg-background/50 rounded-full flex items-center justify-start px-1">
              <div className="w-3 h-3 bg-muted-foreground rounded-full"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )

  const renderActiveTab = () => {
    switch (activeTab) {
      case 'personal': return renderPersonalInfo()
      case 'preferences': return renderPreferences()
      case 'security': return renderSecurity()
      case 'notifications': return renderNotifications()
      default: return renderPersonalInfo()
    }
  }

  if (loading) {
    return (
      <div className="w-full py-6">
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading profile...</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="w-full py-6">
      <div className="py-6">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground">Profile Settings</h1>
          <p className="text-muted-foreground mt-2">Manage your personal information and preferences</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Profile Navigation */}
          <div className="lg:col-span-1">
            <div className="glass-card">
              <nav className="space-y-2">
                {tabs.map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center px-4 py-3 rounded-lg transition-all duration-200 ${activeTab === tab.id
                        ? 'bg-gradient-to-r from-primary to-secondary text-white'
                        : 'text-muted-foreground hover:text-foreground hover:bg-background/50'
                      }`}
                  >
                    <span className="mr-3">{tab.icon}</span>
                    {tab.name}
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Profile Content */}
          <div className="lg:col-span-3">
            <div className="glass-card">
              <div className="mb-6">
                <h2 className="text-xl font-bold text-gradient-esales capitalize">{activeTab} Settings</h2>
                <p className="text-muted-foreground mt-1">
                  {activeTab === 'personal' && 'Update your personal information'}
                  {activeTab === 'preferences' && 'Customize your application preferences'}
                  {activeTab === 'security' && 'Manage your account security settings'}
                  {activeTab === 'notifications' && 'Configure your notification preferences'}
                </p>
              </div>

              {renderActiveTab()}

              {/* Action Buttons */}
              <div className="flex justify-end space-x-4 mt-8 pt-6 border-t border-border">
                {activeTab === 'notifications' ? (
                  <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                    <span>💾</span>
                    <span>Notification preferences are saved automatically</span>
                  </div>
                ) : (
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="bg-gradient-to-r from-primary to-secondary text-white font-semibold py-2 px-6 rounded-lg hover:from-primary/90 hover:to-secondary/90 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {saving ? 'Saving...' : 'Save Changes'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
